import React, { forwardRef, useImperativeHandle } from 'react';
import styled from 'styled-components';
import { Checkout } from '../../../common/types/checkout';
import { FormDataShape } from '../../../common/hooks/useCheckoutController/useCheckoutController.types';

import { LabelV2 } from '../../atoms/v2/label/label-v2.atom';
import { InputV2, SelectV2 } from '../../atoms/v2/input/input-v2.atom';
import * as S from '../../organisms/classic/payment-methods/payment-methods.organism.style';
import { cvvMaxForBrand } from '../../../common/utils/card-helpers';
import { documentMasks } from '../../../common/utils/document-masks';
import { InputField } from '../input-field/input-field.molecule';
import { encodeBase64Browser } from '../../../common/utils/base64.utils';
import { useMpCardSectionCore } from './useMpCardSectionCore';

type Props = {
  checkout: Checkout;
  data: FormDataShape;
  onChange: (field: keyof FormDataShape, value: string) => void;
  payerEmail?: string;
  backendBaseUrl?: string;
};

export type MpCardSectionRef = {
  createToken: () => Promise<any | null>;
  getPayload: () => any | null;
};

export const MpCardSection = forwardRef<MpCardSectionRef, Props>(
  ({ checkout, data, onChange, payerEmail }, ref) => {
    const core = useMpCardSectionCore({ checkout, data, onChange, payerEmail });
    const {
      t,
      cardNumber,
      cardName,
      expiry,
      cvv,
      installments,
      installmentList,
      onChangeCardNumber,
      setCardName,
      onChangeExpiry,
      onChangeCvv,
      setInstallments,
      createToken,
      getPayload,
      brand,
    } = core;

    useImperativeHandle(ref, () => ({
      createToken,
      getPayload,
    }));
    return (
      <form onSubmit={(e) => e.preventDefault()}>
        <S.CardInputField>
          <LabelV2 htmlFor="mp-core-cardNumber">{t('cardNumber')}</LabelV2>
          <InputV2
            color={
              checkout?.checkoutConfig?.config?.checkoutData?.purchaseButton
                ?.background
            }
            id="mp-core-cardNumber"
            inputMode="numeric"
            autoComplete="cc-number"
            placeholder="0000 0000 0000 0000"
            value={cardNumber}
            onChange={(e) => onChangeCardNumber(e.target.value)}
            maxLength={23}
            data-hj-suppress
          />
        </S.CardInputField>

        <S.CardInputField>
          <LabelV2 htmlFor="mp-core-cardName">{t('cardName')}</LabelV2>
          <InputV2
            color={
              checkout?.checkoutConfig?.config?.checkoutData?.purchaseButton
                ?.background
            }
            id="mp-core-cardName"
            placeholder={t('cardNamePlaceholder')}
            value={cardName}
            onChange={(e) => setCardName(e.target.value)}
            autoComplete="cc-name"
            data-hj-suppress
          />
        </S.CardInputField>

        <TwoCols>
          <S.CardInputField style={{ flex: 1 }}>
            <LabelV2 htmlFor="mp-core-expiry">{t('expiryDate')}</LabelV2>
            <InputV2
              color={
                checkout?.checkoutConfig?.config?.checkoutData?.purchaseButton
                  ?.background
              }
              id="mp-core-expiry"
              inputMode="numeric"
              autoComplete="cc-exp"
              placeholder="MM/AA"
              value={expiry}
              onChange={(e) => {
                let v = e.target.value.replace(/\D/g, '').slice(0, 4);
                if (v.length >= 3) v = `${v.slice(0, 2)}/${v.slice(2)}`;
                onChange('cardExpiry', v);
                onChangeExpiry(v);
              }}
              maxLength={5}
              data-hj-suppress
            />
          </S.CardInputField>

          <S.CardInputField style={{ flex: 1 }}>
            <LabelV2 htmlFor="mp-core-cvv">CVV</LabelV2>
            <InputV2
              color={
                checkout?.checkoutConfig?.config?.checkoutData?.purchaseButton
                  ?.background
              }
              id="mp-core-cvv"
              inputMode="numeric"
              autoComplete="cc-csc"
              placeholder={brand === 'amex' ? '0000' : '000'}
              value={cvv}
              onChange={(e) => {
                const only = e.target.value.replace(/\D/g, '');
                const max = cvvMaxForBrand(brand);
                const next = only.slice(0, max);
                const b64 = encodeBase64Browser(next);
                onChangeCvv(next);
                onChange('cvvCardId', b64);
                onChange('cardCvv', next);
              }}
              maxLength={cvvMaxForBrand(brand)}
              data-hj-suppress
            />
          </S.CardInputField>
        </TwoCols>

        <S.CardInputField>
          <InputField
            color={
              checkout?.checkoutConfig?.config?.checkoutData?.purchaseButton
                ?.background
            }
            id="cpf"
            label={t('document')}
            placeholder={t('fillYourDocument')}
            mask={documentMasks[checkout?.paisEnum]}
            maskChar=""
            value={data.cpf}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              onChange('cpf', e.target.value)
            }
            suppressAnalytics={true}
          />
        </S.CardInputField>

        <S.CardInputField>
          <LabelV2 htmlFor="mp-core-installments">{t('installment')}</LabelV2>
          <SelectV2
            color={
              checkout?.checkoutConfig?.config?.checkoutData?.purchaseButton
                ?.background
            }
            id="mp-core-installments"
            value={String(installments)}
            onChange={(e) => setInstallments(Number(e.target.value))}
          >
            {installmentList.length === 0 ? (
              <option value="1"></option>
            ) : (
              installmentList.map((opt) => (
                <option key={opt.installments} value={String(opt.installments)}>
                  {opt.recommended_message}
                </option>
              ))
            )}
          </SelectV2>
        </S.CardInputField>
      </form>
    );
  },
);

const TwoCols = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;
